// ============================================================================
// deepLink.js — URLs profondes partageables (deep-links) Aramis
// ============================================================================
// Utilisé par App.jsx (navTo + consumeNavParams) ET par les modules détaillés
// (ConsultationAsync, VeilleModule, Themis, etc.) qui veulent exposer un
// bouton « 🔗 Copier le lien » sur leurs fiches individuelles.
//
// Format de l'URL : ?view=<view>&<param1>=<val1>&<param2>=<val2>
// Ex : /?view=athos-projets&projectId=abc&mode=expand
// ============================================================================

import React, { useState } from "react";
import { IconeTexte } from "./icones";

// Pointeur grossier (doigt) — patron du depot (cf. src/InviteSelectorModal.js).
// Reserve au tactile ce qu'aucune variable CSS ne sait porter (ici : le
// remplacement du libelle par une icone, et la densite du bouton compact).
const POINTEUR_GROSSIER = typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(pointer: coarse)").matches : false;

// Helper : construit une URL profonde absolue partageable
// Ex : buildDeepLink("athos-projets", { projectId: "abc", mode: "expand" })
//   → "https://aramis.enio.fr/?view=athos-projets&projectId=abc&mode=expand"
export function buildDeepLink(view, params = null) {
  try {
    const qp = new URLSearchParams();
    qp.set("view", view);
    if (params && typeof params === "object") {
      for (const [k, val] of Object.entries(params)) {
        if (k.startsWith("_") || val == null) continue;
        if (typeof val === "object") continue;
        qp.set(k, String(val));
      }
    }
    return window.location.origin + window.location.pathname + "?" + qp.toString();
  } catch {
    return window.location.href;
  }
}

/** Copie le lien direct (?view=…&…) d'une fiche : l'API Presse-papiers, puis
 *  le repli d'avant (zone de texte sélectionnée, execCommand) là où elle
 *  manque ou refuse. Rend true si la copie a réussi. Source unique du bouton
 *  ci-dessous et des menus qui proposent « Lien Aramis » (Veille des marchés). */
export async function copierLienDirect(view, params = null) {
  const url = buildDeepLink(view, params);
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch { /* repli ci-dessous */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = url; document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy") !== false; } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Composant : bouton « 🔗 Copier le lien » réutilisable.
// Place-le où tu veux dans une fiche détaillée (projet, réunion, membre,
// AO, consultation, session, sondage…) pour permettre le partage
// d'URL directe.
//
// Props :
//   view (string) — l'identifiant de la vue cible (ex: "athos-projets")
//   params (object|null) — paramètres scalaires injectés en query string
//   label (string) — texte du bouton (default: "Copier le lien")
//   title (string) — tooltip
//   compact (bool) — version réduite (icône seule)
//   surFond ("clair" | "fonce") — posé sur un fond CODÉ EN DUR (fiche projet
//     dépliée #fff, bandeau en dégradé foncé) : encres fixes, qui ne suivent
//     pas le thème (relecture L10b, tour 3). Absent : encres du thème.
export function CopyLinkButton({ view, params = null, label = "Copier le lien", title, compact = false, style = null, surFond = null }) {
  const [copied, setCopied] = useState(false);
  // Compact SANS libellé = bouton icône posé sur une ligne de liste : AU DOIGT il
  // doit rester carré et de largeur CONSTANTE, sinon le passage à « ✓ Lien copié »
  // triple sa largeur et réorganise toute la ligne sous le doigt de l'utilisateur.
  // Au clavier-souris, le bouton garde son libellé et sa densité d'origine.
  const iconeSeule = POINTEUR_GROSSIER && compact && !String(label || "").trim();
  const onCopy = async () => {
    // Le repli des navigateurs sans Clipboard API vit dans copierLienDirect.
    if (await copierLienDirect(view, params)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button
      onClick={onCopy}
      title={title || "Copier l'URL directe vers cette fiche"}
      style={{
        background: copied ? "rgba(148, 193, 31,.15)" : "transparent",
        border: "1px solid " + (copied ? "#94C11F" : "var(--border-color,#ccc)"),
        color: copied
          ? (surFond === "clair" ? "var(--acc-green-ink)" : surFond === "fonce" ? "var(--acc-green)" : "var(--tx-green)")
          : (surFond === "clair" ? "var(--acc-gray-ink)" : "var(--text-sub,#666)"),
        borderRadius: "var(--radius-sm)", cursor: "pointer",
        // Gabarit tactile en version compacte : ces boutons vivent sur des lignes de
        // liste, où ils mesuraient ~18 pt de haut — plus petit que l'étoile de favori
        // voisine. TOUT cela est réservé au doigt : au clavier-souris on retrouve
        // « 2px 8px » / 10px, et --cible-min y vaut 0 (donc aucun plancher).
        padding: iconeSeule ? 0
          : (compact ? (POINTEUR_GROSSIER ? "0 10px" : "2px 8px") : "4px 10px"),
        minWidth: iconeSeule ? 44 : undefined,
        minHeight: compact ? "var(--cible-min)" : undefined,
        justifyContent: iconeSeule ? "center" : undefined,
        fontSize: compact ? (POINTEUR_GROSSIER ? "var(--fs-meta)" : "var(--fs-micro)") : "var(--fs-micro)", fontWeight: 600,
        fontFamily: "inherit",
        display: "inline-flex", alignItems: "center", gap: 4,
        transition: POINTEUR_GROSSIER
          ? "background .15s,border-color .15s,color .15s" : "all .15s",
        ...(style || {}),
      }}>
      {iconeSeule
        ? (copied ? "✓" : <><IconeTexte nom="link"/></>)
        : (copied ? "✓ Lien copié" : <><IconeTexte nom="link"/>{` ${label}`}</>)}
    </button>
  );
}
